import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Activity, FileCheck2, Gavel, MessageSquareText, ScanLine, ShieldAlert, CheckCircle2 } from "lucide-react";
import { useI18n } from "../../i18n";
import Navbar from "../../components/marketing/Navbar";
import Footer from "../../components/marketing/Footer";
import { TaxServicesSection } from "../../components/marketing/TaxServices";
import { Button } from "../../components/ui/button";

const PIPELINE = ["step_submission", "step_intake", "step_ai", "step_specialist", "step_supervisor", "step_quality", "step_deliverable"];
const FEATURES = [
  ["feat_health_t", "feat_health_d", Activity],
  ["feat_return_t", "feat_return_d", FileCheck2],
  ["feat_zatca_t", "feat_zatca_d", Gavel],
  ["feat_advisory_t", "feat_advisory_d", MessageSquareText],
  ["feat_docs_t", "feat_docs_d", ScanLine],
  ["feat_risk_t", "feat_risk_d", ShieldAlert],
];

export default function Home() {
  const { t, dir } = useI18n();
  const nav = useNavigate();
  const Arrow = dir === "rtl" ? ArrowLeft : ArrowRight;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative gc-gradient-navy text-white overflow-hidden">
        <div className="absolute inset-0 gc-hero-grid opacity-40" />
        <div className="absolute -top-24 end-0 h-96 w-96 rounded-full blur-3xl" style={{ background: "hsl(158 64% 28% / 0.25)" }} />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs tracking-wide text-white/80">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: "hsl(41 72% 55%)" }} /> {t("secondary_pos")}
            </span>
            <h1 className="mt-6 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">{t("hero_title")}</h1>
            <p className="mt-6 text-lg lg:text-xl text-white/70 max-w-2xl leading-relaxed">{t("hero_sub")}</p>
            <p className="mt-3 text-base text-white/50 max-w-2xl">{t("positioning")}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button data-testid="hero-check-btn" onClick={() => nav("/diagnostic")} className="rounded-full h-12 px-7 text-base bg-[hsl(158_64%_32%)] hover:bg-[hsl(158_64%_27%)]">
                {t("cta_check")} <Arrow className="ms-2 h-4 w-4" />
              </Button>
              <Button data-testid="hero-plans-btn" onClick={() => nav("/pricing")} variant="outline" className="rounded-full h-12 px-7 text-base bg-transparent border-white/25 text-white hover:bg-white/10">
                {t("cta_plans")}
              </Button>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              {[["13", t("vat_health_score")], ["6", t("step_quality")], ["100%", t("audit_trail")]].map(([n, l], i) => (
                <div key={i}>
                  <div className="font-mono text-3xl font-bold text-[hsl(41_72%_60%)]">{n}</div>
                  <div className="text-xs text-white/50 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Three taxes */}
      <TaxServicesSection onOpen={(slug) => nav(`/tax/${slug}`)} />

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[hsl(217_63%_15%)]">{t("how_title")}</h2>
          <p className="mt-4 text-muted-foreground text-lg">{t("how_sub")}</p>
        </div>
        <div className="mt-12 flex flex-wrap items-stretch gap-3">
          {PIPELINE.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex-1 min-w-[140px] rounded-xl border border-border bg-card p-4 gc-ring-shadow">
                <div className="font-mono text-xs text-[hsl(158_64%_28%)]">{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-2 font-semibold text-sm text-[hsl(217_63%_15%)]">{t(s)}</div>
              </div>
              {i < PIPELINE.length - 1 && <div className="self-center hidden sm:block"><Arrow className="h-4 w-4 text-muted-foreground" /></div>}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-secondary/50 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-3 gap-5">
            {FEATURES.map(([tk, dk, Icon], i) => (
              <motion.div key={tk} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-border bg-card p-7 hover:border-[hsl(158_64%_28%)] transition-colors group">
                <div className="h-11 w-11 rounded-xl bg-[hsl(217_63%_15%)] flex items-center justify-center group-hover:bg-[hsl(158_64%_28%)] transition-colors">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="mt-5 font-display font-semibold text-lg text-[hsl(217_63%_15%)]">{t(tk)}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(dk)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-5xl mx-auto px-6 py-24 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(158_64%_96%)] text-[hsl(158_64%_22%)] px-4 py-1.5 text-xs font-medium tracking-wide">
          <CheckCircle2 className="h-4 w-4" /> {t("secondary_pos")}
        </div>
        <h2 className="mt-6 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[hsl(217_63%_15%)]">{t("trust_title")}</h2>
        <p className="mt-5 text-lg text-muted-foreground leading-relaxed">{t("trust_body")}</p>
        <Button data-testid="trust-cta-btn" onClick={() => nav("/diagnostic")} className="mt-8 rounded-full h-12 px-8 bg-[hsl(217_63%_15%)] hover:bg-[hsl(217_63%_20%)]">
          {t("cta_start")} <Arrow className="ms-2 h-4 w-4" />
        </Button>
      </section>

      <Footer />
    </div>
  );
}
